import { DEFAULT_BRANDING_COMPANY_NAME } from '@config/config';
import { CompanyBrandingData } from '@customTypes/CompanyBranding';
import { DEFAULT_BRANDING } from '@constants/branding';

export class BrandingDOMUtils {
  private static readonly BORDER_RADIUS_MAP = {
    small: '4px',
    medium: '8px',
    large: '16px',
  } as const;

  private static readonly CSS_VARIABLES = {
    PRIMARY: '--color-primary',
    SECONDARY: '--color-secondary',
    BORDER_RADIUS: '--border-radius',
    THEME_MODE: '--theme-mode',
    BODY_BG: '--body-bg',
    SIDEBAR_BG: '--sidebar-bg',
    CONTENT_BG: '--content-bg',
    COLOR_BODY_TEXT: '--color-body-text',
    COLOR_TABLE_ROW_HOVER: '--color-table-row-hover',
    TABLE_RADIUS: '--table-radius',
    BORDER_RADIUS_LARGE: '--border-radius-large',
    BORDER_RADIUS_MEDIUM: '--border-radius-medium',
    BORDER_RADIUS_SMALL: '--border-radius-small',
    FONT_FAMILY: '--font-family',
    BORDER_RADIUS_DEFAULT: '--border-radius-default',
  } as const;

  static applyBranding(branding: CompanyBrandingData): void {
    try {
      const root = document.documentElement;

      // Apply color palettes
      this.applyColorPalettes(root, branding);

      // Apply base colors
      this.applyBaseColors(root, branding);

      // Apply layout properties
      this.applyLayoutProperties(root, branding);

      // Apply theme
      this.applyTheme(root, branding);

      // Apply logo
      this.updateLogo(branding.logo_url || '');
    } catch (error) {
      console.error('Failed to apply branding:', error);
    }
  }

  static updateLogo(logoUrl: string): void {
    try {
      const logoElements = document.querySelectorAll('[data-brand-logo]');
      logoElements.forEach((el: Element) => {
        const img = el as HTMLImageElement;
        if (logoUrl && img.src !== logoUrl) {
          img.src = logoUrl;
        }
      });
    } catch (error) {
      console.error('Failed to update logo:', error);
    }
  }

  static getBorderRadiusValue(radius: 'small' | 'medium' | 'large'): string {
    return this.BORDER_RADIUS_MAP[radius] || this.BORDER_RADIUS_MAP.medium;
  }

  static loadFont(fontFamily: string): void {
    const fontMap: Record<string, string> = {
      Manrope: 'Manrope:wght@400;500;600;700',
      Inter: 'Inter:wght@400;500;600;700',
      Roboto: 'Roboto:wght@400;500;600;700',
      'Open Sans': 'Open+Sans:wght@400;500;600;700',
      Lato: 'Lato:wght@400;500;600;700',
      Montserrat: 'Montserrat:wght@400;500;600;700',
    };

    const fontQuery = fontMap[fontFamily];
    if (!fontQuery) return;

    // Check if font is already loaded
    const existingLink = document.querySelector(`link[href*="${fontQuery}"]`);
    if (existingLink) return;

    try {
      const link = document.createElement('link');
      link.href = `https://fonts.googleapis.com/css2?family=${fontQuery}&display=swap`;
      link.rel = 'stylesheet';
      link.onerror = () => console.warn(`Failed to load font: ${fontFamily}`);
      document.head.appendChild(link);
    } catch (error) {
      console.error(`Failed to load font ${fontFamily}:`, error);
    }
  }

  // Private helper methods
  private static applyColorPalettes(
    root: HTMLElement,
    branding: CompanyBrandingData,
  ): void {
    // Apply primary palette
    if (branding.primary_palette) {
      Object.entries(branding.primary_palette).forEach(([shade, color]) => {
        if (color) {
          root.style.setProperty(`--color-primary-${shade}`, color);
        }
      });
    }

    // Apply secondary palette
    if (branding.secondary_palette) {
      Object.entries(branding.secondary_palette).forEach(([shade, color]) => {
        if (color) {
          root.style.setProperty(`--color-secondary-${shade}`, color);
        }
      });
    }

    // Apply accent palette
    if (branding.accent_palette) {
      Object.entries(branding.accent_palette).forEach(([shade, color]) => {
        if (color) {
          root.style.setProperty(`--color-accent-${shade}`, color);
        }
      });
    }
  }

  private static applyBaseColors(
    root: HTMLElement,
    branding: CompanyBrandingData,
  ): void {
    this.setCSSVariable(
      root,
      this.CSS_VARIABLES.PRIMARY,
      branding.primary_color,
    );
    this.setCSSVariable(
      root,
      this.CSS_VARIABLES.SECONDARY,
      branding.secondary_color,
    );
  }

  private static applyLayoutProperties(
    root: HTMLElement,
    branding: CompanyBrandingData,
  ): void {
    const { additional_data } = branding;

    // Border radius properties
    this.setCSSVariable(
      root,
      this.CSS_VARIABLES.BORDER_RADIUS,
      additional_data.border_radius || 'medium',
    );

    this.setCSSVariable(
      root,
      this.CSS_VARIABLES.BORDER_RADIUS_DEFAULT,
      this.getBorderRadiusValue(
        additional_data.border_radius as 'small' | 'medium' | 'large',
      ),
    );

    // Background colors
    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.BODY_BG,
      additional_data.body_bg,
      DEFAULT_BRANDING.additional_data.body_bg,
    );

    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.SIDEBAR_BG,
      additional_data.sidebar_bg,
      DEFAULT_BRANDING.additional_data.sidebar_bg,
    );

    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.CONTENT_BG,
      additional_data.content_bg,
      DEFAULT_BRANDING.additional_data.content_bg,
    );

    // Text colors
    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.COLOR_BODY_TEXT,
      additional_data.color_body_text,
      DEFAULT_BRANDING.additional_data.color_body_text,
    );

    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.COLOR_TABLE_ROW_HOVER,
      additional_data.color_table_row_hover,
      DEFAULT_BRANDING.additional_data.color_table_row_hover,
    );

    // Radius properties
    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.TABLE_RADIUS,
      additional_data.table_radius,
      DEFAULT_BRANDING.additional_data.table_radius,
      '12px',
    );

    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.BORDER_RADIUS_LARGE,
      additional_data.border_radius_large,
      DEFAULT_BRANDING.additional_data.border_radius_large,
      '12px',
    );

    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.BORDER_RADIUS_MEDIUM,
      additional_data.border_radius_medium,
      DEFAULT_BRANDING.additional_data.border_radius_medium,
      '8px',
    );

    this.setCSSVariableWithFallback(
      root,
      this.CSS_VARIABLES.BORDER_RADIUS_SMALL,
      additional_data.border_radius_small,
      DEFAULT_BRANDING.additional_data.border_radius_small,
      '4px',
    );
  }

  private static applyTheme(
    root: HTMLElement,
    branding: CompanyBrandingData,
  ): void {
    // Font family
    this.setCSSVariable(
      root,
      this.CSS_VARIABLES.FONT_FAMILY,
      branding.font_family || 'Manrope',
    );

    // Theme mode
    this.setCSSVariable(
      root,
      this.CSS_VARIABLES.THEME_MODE,
      branding.theme_mode || 'light',
    );
    root.setAttribute('data-theme', branding.theme_mode || 'light');
  }

  private static setCSSVariable(
    root: HTMLElement,
    property: string,
    value: string | undefined,
  ): void {
    if (value) {
      root.style.setProperty(property, value);
    }
  }

  private static setCSSVariableWithFallback(
    root: HTMLElement,
    property: string,
    value: string | undefined,
    fallbackValue: string | undefined,
    defaultValue = '',
  ): void {
    const finalValue = value || fallbackValue || defaultValue;
    if (finalValue) {
      root.style.setProperty(property, finalValue);
    }
  }

  static updateMetaInfo(data: CompanyBrandingData): void {
    const faviconUrl = data?.additional_data?.favicon_url;
    let title = 'Percuity';
    if (data.app_name !== DEFAULT_BRANDING_COMPANY_NAME) {
      // faviconUrl = `/assets/${companyName}-favicon.png`;
      title = data.app_name;
    }

    // // Find existing favicon or create new one
    const faviconLinkEl = document.querySelector(
      "link[rel*='icon']",
    ) as HTMLLinkElement;

    if (faviconUrl) {
      faviconLinkEl.href = faviconUrl;
    }

    const titleEl = document.querySelector('title') as HTMLTitleElement;
    if (titleEl && title) {
      titleEl.textContent = title;
    }
  }
}
